'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { COMPANY } from '@/lib/company';
import { createClient } from '@/lib/supabase/client';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

interface LiveStats {
  employees: number | null;
  departments: number | null;
  omanizationPct: number | null;
  branches: number | null;
  openPositions: number | null;
}

export default function LoginContent() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [currentYear, setCurrentYear] = useState<number | null>(null);
  const [liveStats, setLiveStats] = useState<LiveStats>({
    employees: null,
    departments: null,
    omanizationPct: null,
    branches: null,
    openPositions: null,
  });

  const router = useRouter();
  const searchParams = useSearchParams();
  const { signIn, user, loading } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', remember: false },
  });

  useEffect(() => {
    setMounted(true);
    setCurrentYear(new Date().getFullYear());
  }, []);

  // Fetch live stats from DB
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const supabase = createClient();
        const [empRes, deptRes] = await Promise.all([
          supabase.from('employees').select('id', { count: 'exact', head: true }).eq('employment_status', 'active'),
          supabase.from('departments').select('id', { count: 'exact', head: true }),
        ]);
        setLiveStats({
          employees: empRes.count ?? null,
          departments: deptRes.count ?? null,
          omanizationPct: null,
          branches: null,
          openPositions: null,
        });
      } catch {
        // silent
      }
    };
    fetchStats();
  }, []);

  // Redirect if already logged in
  useEffect(() => {
    if (!loading && user) {
      const redirectTo = searchParams?.get('next') || searchParams?.get('redirectTo') || '/my-dashboard';
      router.replace(redirectTo);
    }
  }, [user, loading, router, searchParams]);

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const result = await signIn(data.email, data.password);
      if (result?.error) {
        toast.error(result.error.message || 'Invalid email or password');
      } else {
        toast.success('Welcome back!');
        const redirectTo = searchParams?.get('next') || searchParams?.get('redirectTo') || '/my-dashboard';
        router.replace(redirectTo);
      }
    } catch (err: any) {
      toast.error(err?.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!mounted) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex"
      style={{ background: 'linear-gradient(135deg, #0f1f3d 0%, #1a3a6b 50%, #0f1f3d 100%)' }}
    >
      {/* Left Panel — Branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col items-center justify-center px-12 relative">
        {/* Background pattern */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, #4a90d9 0%, transparent 50%), radial-gradient(circle at 75% 75%, #2563eb 0%, transparent 50%)`,
          }}
        />

        <div className="relative z-10 flex flex-col items-center text-center max-w-md">
          {/* Logo */}
          <div className="w-28 h-28 flex items-center justify-center rounded-3xl bg-white/10 backdrop-blur-sm p-4 shadow-2xl border border-white/20 mb-8">
            <Image
              src={COMPANY.logoPath}
              alt={`${COMPANY.name} Logo`}
              width={96}
              height={96}
              className="object-contain"
              priority
            />
          </div>

          <h1 className="text-white text-3xl font-bold tracking-tight leading-tight mb-3">
            {COMPANY.name}
          </h1>
          <p className="text-blue-200/70 text-base font-medium tracking-widest uppercase mb-2">
            HR Management System
          </p>
          <p className="text-blue-300/50 text-sm mb-10">
            {COMPANY.address.city}, {COMPANY.address.country}
          </p>

          {/* Live Stats */}
          {(liveStats.employees !== null || liveStats.departments !== null) && (
            <div className="grid grid-cols-2 gap-4 w-full">
              {liveStats.employees !== null && (
                <div className="bg-white/8 rounded-2xl p-4 border border-white/10 text-center">
                  <p className="text-white text-2xl font-bold">{liveStats.employees}</p>
                  <p className="text-blue-200/60 text-xs mt-1">Active Employees</p>
                </div>
              )}
              {liveStats.departments !== null && (
                <div className="bg-white/8 rounded-2xl p-4 border border-white/10 text-center">
                  <p className="text-white text-2xl font-bold">{liveStats.departments}</p>
                  <p className="text-blue-200/60 text-xs mt-1">Departments</p>
                </div>
              )}
            </div>
          )}

          {/* Feature highlights */}
          <div className="mt-10 space-y-3 w-full text-left">
            {[
              { icon: 'UserGroupIcon', label: 'Employee & Payroll Management' },
              { icon: 'ClockIcon', label: 'Attendance & Leave Tracking' },
              { icon: 'ChartBarIcon', label: 'KPI & Performance Analytics' },
              { icon: 'ShieldCheckIcon', label: 'Role-Based Access Control' },
            ].map((f) => (
              <div key={f.label} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center shrink-0">
                  <Icon name={f.icon} size={16} className="text-blue-300" />
                </div>
                <span className="text-blue-100/70 text-sm">{f.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel — Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex flex-col items-center mb-8 lg:hidden">
            <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-white/10 p-3 mb-4 border border-white/20">
              <Image
                src={COMPANY.logoPath}
                alt={`${COMPANY.name} Logo`}
                width={64}
                height={64}
                className="object-contain"
                priority
              />
            </div>
            <h1 className="text-white text-xl font-bold text-center">{COMPANY.name}</h1>
            <p className="text-blue-200/60 text-xs mt-1 tracking-widest uppercase">HR Management System</p>
          </div>

          {/* Card */}
          <div className="bg-white rounded-3xl shadow-2xl p-8">
            <div className="mb-7">
              <h2 className="text-gray-900 text-2xl font-bold">Welcome back</h2>
              <p className="text-gray-500 text-sm mt-1">Sign in to your account to continue</p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
              {/* Email */}
              <div>
                <label className="block text-sm font-600 text-gray-700 mb-1.5">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Icon name="EnvelopeIcon" size={17} className="text-gray-400" />
                  </div>
                  <input
                    type="email"
                    autoComplete="email"
                    placeholder="you@ecityoman.com"
                    className={`w-full pl-10 pr-4 py-3 rounded-xl border text-sm transition-all outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                      errors.email ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    }`}
                    {...register('email', {
                      required: 'Email is required',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                    })}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                    <Icon name="ExclamationCircleIcon" size={12} />
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-600 text-gray-700">Password</label>
                  <Link href="/forgot-password" className="text-xs text-blue-600 hover:text-blue-700 font-500 transition-colors">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Icon name="LockClosedIcon" size={17} className="text-gray-400" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className={`w-full pl-10 pr-11 py-3 rounded-xl border text-sm transition-all outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                      errors.password ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    }`}
                    {...register('password', { required: 'Password is required' })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={17} />
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                    <Icon name="ExclamationCircleIcon" size={12} />
                    {errors.password.message}
                  </p>
                )}
              </div>

              {/* Remember me */}
              <div className="flex items-center gap-2.5">
                <input
                  type="checkbox"
                  id="remember"
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 accent-blue-600"
                  {...register('remember')}
                />
                <label htmlFor="remember" className="text-sm text-gray-600 cursor-pointer select-none">
                  Keep me signed in
                </label>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-600 rounded-xl transition-all text-sm shadow-sm hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <Icon name="ArrowRightOnRectangleIcon" size={17} />
                    Sign In
                  </>
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="mt-6 pt-5 border-t border-gray-100 text-center">
              <p className="text-xs text-gray-400">
                © {currentYear ?? 2026} {COMPANY.name}. All rights reserved.
              </p>
              <p className="text-xs text-gray-400 mt-0.5">{COMPANY.website}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
